export const API_BASE = import.meta.env?.PUBLIC_API_URL ?? "http://localhost:4000";
const IS_STATIC = typeof __STATIC_BUILD__ !== "undefined" && !!__STATIC_BUILD__;

async function handleResponse(res) {
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || res.statusText);
  }
  return res.json();
}

export async function register({ name, email, companyName, password }) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, email, companyName, password }),
    })
  );
}

export async function login({ email, password }) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    })
  );
}

export async function updateProfile(token, updates) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/users/me`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(updates),
    })
  );
}

export async function deleteAccount(token) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/users/me`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    })
  );
}

export async function fetchPageContent(slug) {
  if (IS_STATIC) {
    const res = await fetch(`/static-data/content-${slug}.json`);
    if (!res.ok) throw new Error(res.statusText);
    return res.json();
  }
  const res = await fetch(`${API_BASE}/api/content/${encodeURIComponent(slug)}`);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || res.statusText);
  }
  return res.json();
}

export async function adminSyncContent(token, payload) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/content/sync`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    })
  );
}

export async function fetchCategories() {
  if (IS_STATIC) {
    const res = await fetch(`/static-data/categories.json`);
    if (!res.ok) throw new Error(res.statusText);
    return res.json();
  }
  const res = await fetch(`${API_BASE}/categories`);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || res.statusText);
  }
  return res.json();
}

export async function fetchCatalog() {
  if (IS_STATIC) {
    const res = await fetch(`/static-data/catalog.json`);
    if (!res.ok) throw new Error(res.statusText);
    return res.json();
  }
  const res = await fetch(`${API_BASE}/catalog`);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || res.statusText);
  }
  return res.json();
}

export async function fetchAllUsers(token) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/admin/users`, {
      headers: { Authorization: `Bearer ${token}` },
    })
  );
}

export async function adminCreateUser(token, data) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/admin/users`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    })
  );
}

export async function adminUpdateUser(token, id, data) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(id)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    })
  );
}

export async function adminDeleteUser(token, id) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    })
  );
}

export async function adminFetchCatalogCategories(token) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/admin/catalog/categories`, {
      headers: { Authorization: `Bearer ${token}` },
    })
  );
}

export async function adminCreateCatalogCategory(token, data) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/admin/catalog/categories`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    })
  );
}

export async function adminUpdateCatalogCategory(token, name, data) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/admin/catalog/categories/${encodeURIComponent(name)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    })
  );
}

export async function adminDeleteCatalogCategory(token, name) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/admin/catalog/categories/${encodeURIComponent(name)}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    })
  );
}

export async function adminAddCatalogSubcategory(token, categoryName, name) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/admin/catalog/categories/${encodeURIComponent(categoryName)}/subcategories`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ name }),
    })
  );
}

export async function adminRenameCatalogSubcategory(token, categoryName, oldName, newName) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/admin/catalog/categories/${encodeURIComponent(categoryName)}/subcategories/${encodeURIComponent(oldName)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ name: newName }),
    })
  );
}

export async function adminDeleteCatalogSubcategory(token, categoryName, name) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/admin/catalog/categories/${encodeURIComponent(categoryName)}/subcategories/${encodeURIComponent(name)}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    })
  );
}

export async function adminFetchAllCatalogItems(token, { category } = {}) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  const url = new URL(`${API_BASE}/api/admin/catalog/items`);
  if (category !== undefined) url.searchParams.set("category", category);
  return handleResponse(await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } }));
}

export async function adminCreateCatalogItem(token, data) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/admin/catalog/items`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    })
  );
}

export async function adminUpdateCatalogItem(token, sku, data) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/admin/catalog/items/${encodeURIComponent(sku)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    })
  );
}

export async function adminDeleteCatalogItem(token, sku) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/admin/catalog/items/${encodeURIComponent(sku)}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    })
  );
}

export async function adminBulkCatalogItems(token, payload) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/admin/catalog/items/bulk`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    })
  );
}

export async function adminUploadCatalogImage(token, payload) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/admin/catalog/upload-image`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    })
  );
}

export async function adminImportCatalog(token, items) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/admin/catalog/import`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ items }),
    })
  );
}

export async function submitSalesInquiry(data, token) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/contact/sales`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(data),
    })
  );
}

export async function submitSupportRequest(token, data) {
  if (IS_STATIC) throw new Error("Not available — contact us at sales@pmpmedical.biz");
  return handleResponse(
    await fetch(`${API_BASE}/api/contact/support`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    })
  );
}

export async function fetchCatalogItem(sku) {
  if (IS_STATIC) {
    const res = await fetch(`/static-data/catalog-item-${encodeURIComponent(sku)}.json`);
    if (!res.ok) {
      const err = new Error("Not found");
      if (res.status === 404) err.notFound = true;
      throw err;
    }
    return res.json();
  }
  const res = await fetch(`${API_BASE}/catalog/${encodeURIComponent(sku)}`);
  if (!res.ok) {
    const text = await res.text();
    const err = new Error(text || res.statusText);
    if (res.status === 404) err.notFound = true;
    throw err;
  }
  return res.json();
}
